import { useMemo, useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BffError, getBackupKey, getShareTargets, getVault, shareBackup, type BackupRow } from '../transport/relay'
import type { StoredVault } from '../transport/relay'
import { FaceIdButton, useFaceId } from './FaceIdButton'
import { FaceIdFailed, unlockWithFaceId } from './passkey'
import { WrongSecret, openVault, sealToVault, unlockWithPassword, unwrapBackupKey } from './vaultCrypto'

/**
 * SHARE A BACKUP WITH A FRIEND — owner, 2026-09-30: 使用者可以選擇把它分享給他的好朋友 …
 * 握手的協定之後, 在他好朋友的雲端系統上面複製一份 … 使用他好朋友自己的 Vault 來加密.
 *
 * This device unlocks the sharer's vault, opens the backup's key and seals it to the
 * FRIEND'S vault (sealToVault) — the cloud gets a sealed box and nothing else. Nothing is
 * copied until the friend accepts; until then the sharer can cancel, and after it the copy
 * is theirs (the owner's rulings, said on this screen).
 */
const VAULT_USERNAME = 'Avengents backup vault'

const label = (f: { name?: string | null; email?: string | null }) => f.name || f.email || 'A friend'

export function ShareForm({ backup, onClose }: { backup: BackupRow; onClose: () => void }) {
  const qc = useQueryClient()
  const targets = useQuery({ queryKey: ['backup-share-targets'], queryFn: getShareTargets, staleTime: 30_000 })
  const vault = useQuery({ queryKey: ['backup-vault'], queryFn: getVault, staleTime: 30_000 })
  const friends = useMemo(() => targets.data?.friends ?? [], [targets.data])
  const [account, setAccount] = useState('')
  const [pw, setPw] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [sentTo, setSentTo] = useState<string | null>(null)

  const target = friends.find((f) => f.account === account) ?? null
  const readyButPw = !!target?.has_vault && !!target.vpk && !!vault.data?.vault
  const ready = readyButPw && !!pw
  const faceId = useFaceId(vault.data?.vault)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (ready) void run((v) => unlockWithPassword(v, pw))
  }

  /** The share, from whichever door opened the vault key: the password or Face ID. */
  const run = async (open: (v: StoredVault) => Promise<Uint8Array<ArrayBuffer>>) => {
    if (!readyButPw || busy || !target?.vpk || !vault.data?.vault) return
    setBusy(true)
    setErr(null)
    try {
      const v = vault.data.vault
      const vk = await open(v)
      const priv = await openVault(v, vk)
      vk.fill(0)
      const key = await getBackupKey(backup.backup_id)
      const dek = await unwrapBackupKey(key.wrapped_dek, priv, v.vpk, backup.backup_id)
      const sealed = await sealToVault(dek, target.vpk, backup.backup_id)
      dek.fill(0)
      await shareBackup({ backup_id: backup.backup_id, account: target.account, vpk: target.vpk, wrapped_dek: sealed })
      setSentTo(label(target))
      void qc.invalidateQueries({ queryKey: ['backup-shares'] })
    } catch (x) {
      if (x instanceof FaceIdFailed) setErr(x.message)
      else if (x instanceof WrongSecret) setErr('That password does not open your vault.')
      else if (x instanceof BffError && (x.code === 'friend_vault_changed' || x.code === 'friend_has_no_vault')) {
        setErr(String((x.body as { message?: unknown }).message ?? x.message))
        void qc.invalidateQueries({ queryKey: ['backup-share-targets'] })
      } else setErr(x instanceof BffError ? String((x.body as { message?: unknown }).message ?? x.message) : String(x instanceof Error ? x.message : x))
    } finally {
      setBusy(false)
    }
  }

  if (sentTo) {
    return (
      <div className="sm-fork bk-form bk-restore" data-testid="share-sent">
        <p className="bk-title">Shared with {sentTo}</p>
        <p className="sm-hint">
          They get a notification. Once they accept, a copy goes into their own backups, encrypted to their vault. You can
          cancel under “Waiting for a friend” until they do; the request expires after 7 days.
        </p>
        <div className="sm-actions">
          <button type="button" className="sheet-btn primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    )
  }

  return (
    <form className="sm-fork bk-form bk-restore" onSubmit={(e) => void submit(e)}>
      <p className="bk-title">Share with a friend</p>
      <ul className="bk-promises">
        <li>
          Your friend gets the whole agent: its full conversation, memory and settings — including anything that was pasted
          into it, like keys or passwords.
        </li>
        <li>You can cancel until they accept. After that the copy is theirs, and it cannot be taken back.</li>
      </ul>
      <div className="sm-field">
        <span className="sm-label">Friend</span>
        {targets.isLoading ? (
          <p className="sm-hint">Reading your friends…</p>
        ) : friends.length === 0 ? (
          <p className="sm-hint">You have no friends on Avengents yet — add one under Humans, then share.</p>
        ) : (
          <select className="sm-input" aria-label="Friend" value={account} disabled={busy} onChange={(e) => setAccount(e.target.value)}>
            <option value="">Choose a friend…</option>
            {friends.map((f) => (
              /* NOT disabled: iOS draws a disabled option like any other and silently refuses
                 the tap (owner, 2026-09-30: 我按下任何一個朋友它都不會跳過去). Every friend can
                 be picked; one without a vault is explained below and Share stays off. */
              <option key={f.account} value={f.account}>
                {label(f)}
                {f.email && f.name ? ` (${f.email})` : ''}
                {f.has_vault ? '' : ' — no backup vault yet'}
              </option>
            ))}
          </select>
        )}
      </div>
      {target && !target.has_vault && (
        <p className="sm-hint bk-bad" role="status">
          {label(target)} has not set up a backup vault yet, so they cannot receive an agent. They get one the first time
          they back up an agent (an agent’s card → Backup this agent). Ask them to, then share again.
        </p>
      )}
      <input type="text" name="username" autoComplete="username" value={VAULT_USERNAME} readOnly tabIndex={-1} aria-hidden="true" className="bk-username" />
      <label className="sm-field">
        <span className="sm-label">Your vault password</span>
        <input
          className="sm-input"
          type="password"
          name="password"
          autoComplete="current-password"
          value={pw}
          disabled={busy}
          onChange={(e) => setPw(e.target.value)}
        />
      </label>
      <p className="sm-hint">It opens the backup on this device, where it is re-sealed to your friend’s vault. We never see either.</p>
      {err && (
        <p className="err-line" role="alert">
          {err}
        </p>
      )}
      <div className="sm-actions">
        <button type="button" className="sheet-btn" disabled={busy} onClick={onClose}>
          Cancel
        </button>
        {faceId && (
          <FaceIdButton
            label="Face ID"
            disabled={!readyButPw || busy}
            onClick={() => void run((v) => unlockWithFaceId(v))}
          />
        )}
        <button type="submit" className="sheet-btn primary" disabled={!ready || busy}>
          {busy ? 'Sharing…' : 'Share'}
        </button>
      </div>
    </form>
  )
}
